// Sezione profilo CRM editabile della scheda cliente: nome/telefono override,
// note (textarea autosize) e tag (chip input). Salva via PATCH inviando SOLO i
// campi cambiati (semantica PATCH del backend). Montata con key={customerKey}
// dal parent → cambiando cliente si rimonta pulita; lo stato locale sopravvive
// invece ai refetch del dettaglio dello STESSO cliente (registra chiamata,
// merge), che non devono azzerare un'eventuale modifica in corso.
import { useRef, useState } from "react";
import { Card } from "@/shared/ui/Card";
import { prettifyApiError } from "@/shared/api";
import { useAutoResizeTextarea } from "@/shared/useAutoResizeTextarea";
import { patchCustomerProfile, type CustomerProfilePatch } from "../api";
import type { CustomerProfile } from "../types";

interface ProfileSectionProps {
  profile: CustomerProfile;
  customerKey: string;
  /** Refetch del dettaglio nel parent (aggiorna H1/alias/KPI dopo il salvataggio). */
  onSaved: () => void;
}

/** Snapshot dei campi persistiti, per il dirty-check e il diff del PATCH. */
interface Snapshot {
  name: string;
  phone: string;
  note: string;
  tags: string[];
}

function sameTags(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((t, i) => t === b[i]);
}

export function ProfileSection({ profile, customerKey, onSaved }: ProfileSectionProps) {
  // Match debole (email assente): niente cliente CRM stabile su cui salvare →
  // il backend risponde 422. Onesto: mostriamo l'hint, nessun form.
  if (profile.isFallbackKey) {
    return (
      <Card title="Profilo" className="mb-4">
        <p className="text-[12px] text-ink-2">
          Cliente riconosciuto per nome+città (email assente): il profilo CRM (note, tag, nome) non
          è salvabile senza un'email stabile.
        </p>
      </Card>
    );
  }

  return <ProfileEditor profile={profile} customerKey={customerKey} onSaved={onSaved} />;
}

function ProfileEditor({ profile, customerKey, onSaved }: ProfileSectionProps) {
  const initial = useRef<Snapshot>({
    name: profile.name,
    phone: profile.phone,
    note: profile.note,
    tags: profile.tags,
  });

  const [name, setName] = useState(profile.name);
  const [phone, setPhone] = useState(profile.phone);
  const [note, setNote] = useState(profile.note);
  const [tags, setTags] = useState<string[]>(profile.tags);
  const [newTag, setNewTag] = useState("");
  const [status, setStatus] = useState<"idle" | "saving" | "saved">("idle");
  const [error, setError] = useState<string | null>(null);

  const noteRef = useRef<HTMLTextAreaElement>(null);
  useAutoResizeTextarea(noteRef, note);

  const nameTrim = name.trim();
  const phoneTrim = phone.trim();
  const dirty =
    nameTrim !== initial.current.name ||
    phoneTrim !== initial.current.phone ||
    note !== initial.current.note ||
    !sameTags(tags, initial.current.tags);

  function addTag() {
    const t = newTag.trim();
    if (!t || tags.includes(t)) {
      setNewTag("");
      return;
    }
    setTags((prev) => [...prev, t]);
    setNewTag("");
  }

  function removeTag(tag: string) {
    setTags((prev) => prev.filter((t) => t !== tag));
  }

  async function handleSave() {
    if (!dirty || status === "saving") return;
    // Diff: solo i campi cambiati finiscono nel body (il backend fa exclude_unset,
    // così non creiamo un override display_name uguale al nome dell'ordine se
    // l'utente ha toccato solo le note).
    const patch: CustomerProfilePatch = {};
    if (nameTrim !== initial.current.name) patch.display_name = nameTrim;
    if (phoneTrim !== initial.current.phone) patch.phone = phoneTrim;
    if (note !== initial.current.note) patch.note = note;
    if (!sameTags(tags, initial.current.tags)) patch.tags = tags;

    setStatus("saving");
    setError(null);
    try {
      await patchCustomerProfile(customerKey, patch);
      // Nuova baseline = valori appena salvati → il bottone torna disabilitato.
      initial.current = { name: nameTrim, phone: phoneTrim, note, tags };
      setName(nameTrim);
      setPhone(phoneTrim);
      setStatus("saved");
      onSaved();
    } catch (err) {
      setStatus("idle");
      setError(prettifyApiError(err));
    }
  }

  const inputCls =
    "w-full rounded-md border border-line bg-card px-3 py-2 text-[13px] text-ink outline-none focus:border-accent focus:ring-2 focus:ring-accent/30 placeholder:text-ink-4";

  return (
    <Card title="Profilo" className="mb-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
          Nome
          <input
            className={inputCls}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setStatus("idle");
            }}
            placeholder="Nome cliente"
            aria-label="Nome cliente"
          />
        </label>
        <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
          Telefono
          <input
            className={inputCls}
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              setStatus("idle");
            }}
            placeholder="+39…"
            aria-label="Telefono cliente"
          />
        </label>
      </div>

      <label className="mt-3 flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
        Note
        <textarea
          ref={noteRef}
          className={`${inputCls} min-h-[64px] resize-none`}
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            setStatus("idle");
          }}
          placeholder="Annotazioni interne sul cliente…"
          aria-label="Note cliente"
        />
      </label>

      <div className="mt-3 flex flex-col gap-1.5">
        <span className="text-[12px] font-semibold text-ink-2">Tag</span>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 rounded-md border border-accent/20 bg-accent/10 px-2 py-0.5 text-[11.5px] text-accent-2"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  aria-label={`Rimuovi tag ${tag}`}
                  className="text-accent-2/70 hover:text-accent-2"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <input
          className={`${inputCls} max-w-[260px]`}
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addTag();
            }
          }}
          placeholder="Aggiungi tag e premi Invio"
          aria-label="Aggiungi tag"
        />
      </div>

      {error && (
        <p role="alert" className="mt-3 text-[12px] text-red">
          {error}
        </p>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={!dirty || status === "saving"}
          className="rounded-md border border-teal bg-teal px-4 py-2 text-[12px] font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {status === "saving" ? "Salvo…" : "Salva"}
        </button>
        {status === "saved" && !dirty && (
          <span className="text-[12px] text-teal" role="status">
            Salvato
          </span>
        )}
      </div>
    </Card>
  );
}
